import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useOrganization } from '@/hooks/useOrganization';
import { useAlerts, useDeleteAlert, type Alert } from '@/hooks/useAlerts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader } from '@/components/ui/loader';
import { PageHeader } from '@/components/layout/PageHeader';
import { AlertDrawer } from '@/components/alerts/AlertDrawer';
import { BellIcon, PlusIcon, HomeIcon, BuildingIcon, EditIcon, TrashIcon, ClockIcon } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { MoreHorizontal } from 'lucide-react';

export const Route = createFileRoute('/_authenticated/org/$orgId/alerts')({
  component: OrganizationAlertsPage,
});

function OrganizationAlertsPage() {
  const { orgId } = Route.useParams();
  const { organization, isLoading } = useOrganization(orgId);
  const toast = useToast();

  const { data: alertsData, isLoading: alertsLoading, error: alertsError } = useAlerts(organization?.id || organization?.slug || orgId);
  const alerts: Alert[] = (alertsData?.alerts || []).map(alert => ({
    ...alert,
    type: alert.type as Alert['type'],
    status: alert.status as Alert['status'],
    severity: alert.severity as Alert['severity'],
    channels: alert.channels as Alert['channels'],
    conditions: alert.conditions || {
      project: '',
      environment: '',
      user: '',
      timeWindow: '',
    },
  }));
  const deleteAlertMutation = useDeleteAlert(organization?.id || '');
  
  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [editingAlert, setEditingAlert] = useState<Alert | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState<Alert | null>(null);
  
  if (alertsError) {
    console.error('Error loading alerts:', alertsError);
  }

  const handleDelete = async () => {
    if (!alertToDelete || !organization?.id) return;
    
    try {
      await deleteAlertMutation.mutateAsync(alertToDelete.id);
      toast.success('Alert deleted successfully');
      setDeleteDialogOpen(false);
      setAlertToDelete(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete alert');
    }
  };

  const handleDeleteClick = (alert: Alert) => {
    setAlertToDelete(alert);
    setDeleteDialogOpen(true);
  };

  const getSeverityColor = (severity: Alert['severity']) => {
    switch (severity) {
      case 'critical':
        return 'destructive';
      case 'high':
        return 'destructive';
      case 'medium':
        return 'default';
      case 'low':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  if (isLoading || alertsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader variant="spinner" size="lg" color="accent" text="Loading alerts..." />
      </div>
    );
  }

  const breadcrumbs = [
    {
      name: 'Dashboard',
      to: '/dashboard',
      icon: HomeIcon,
    },
    {
      name: organization?.name || 'Organization',
      to: `/org/${organization?.slug || orgId}`,
      icon: BuildingIcon,
    },
    {
      name: 'Alerts',
      to: `/org/${organization?.slug || orgId}/alerts`,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={breadcrumbs}
        title="Alerts"
        subtitle={organization ? `Manage notifications for ${organization.name}` : undefined}
        action={
          <Button 
            data-testid="create-alert-button"
            onClick={() => setShowCreateDrawer(true)}
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            New Alert
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Alert Rules</CardTitle>
          <CardDescription>Configure when you want to be notified</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {alerts.map((alert: Alert) => (
              <div
                key={alert.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start space-x-4 flex-1">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <BellIcon className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-sm font-medium text-gray-900">{alert.name}</h3>
                      <Badge variant={getSeverityColor(alert.severity) as any} className="text-xs">
                        {alert.severity}
                      </Badge>
                      <Badge variant={alert.status === 'active' ? 'default' : 'secondary'} className="text-xs">
                        {alert.status === 'active' ? 'Active' : alert.status === 'paused' ? 'Paused' : 'Disabled'}
                      </Badge>
                    </div>
                    {alert.description && (
                      <p className="text-sm text-gray-500 mb-2">{alert.description}</p>
                    )}
                    <div className="flex items-center space-x-4 text-xs text-gray-400">
                      <span className="capitalize">Type: {alert.type.replace('_', ' ')}</span>
                      {alert.channels.length > 0 && (
                        <span>Channels: {alert.channels.join(', ')}</span>
                      )}
                      {alert.triggerCount > 0 && (
                        <span>Triggered {alert.triggerCount} time{alert.triggerCount !== 1 ? 's' : ''}</span>
                      )}
                      {alert.lastTriggered && (
                        <span className="flex items-center">
                          <ClockIcon className="w-3 h-3 mr-1" />
                          Last: {new Date(alert.lastTriggered).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    data-testid={`edit-alert-${alert.id}`}
                    onClick={() => setEditingAlert(alert)}
                  >
                    <EditIcon className="w-4 h-4" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditingAlert(alert)}>
                        <EditIcon className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleDeleteClick(alert)}
                        className="text-destructive"
                      >
                        <TrashIcon className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
            {alerts.length === 0 && (
              <div className="text-center py-12">
                <BellIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No alerts configured</h3>
                <p className="text-gray-500 mb-4">Set up alerts to stay informed</p>
                <Button onClick={() => setShowCreateDrawer(true)}>
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Create Alert
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Alert Drawer */}
      {organization?.id && (
        <AlertDrawer
          organizationId={organization.id}
          alert={editingAlert || undefined}
          open={showCreateDrawer || !!editingAlert}
          onOpenChange={(open) => {
            if (!open) {
              setShowCreateDrawer(false);
              setEditingAlert(null);
            }
          }}
          onSuccess={() => {
            setShowCreateDrawer(false);
            setEditingAlert(null);
          }}
        />
      )}

      {/* Delete Alert Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Alert</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{alertToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteAlertMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteAlertMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

